import {
  type HandwritingAnswer,
  gradeHandwriting,
} from "./grade-handwriting.js";
import type { HandwritingReading } from "./handwriting-verdict.js";
import { lookUpHandwriting } from "../handwriting/handwriting-lookup.js";
import { ratings } from "./rating.js";

/**
 * Marking a character a reader wrote from memory in the writing drill.
 *
 * The drill's fourth stage is marked by the 复习 card's own
 * {@link gradeHandwriting}, over the same index, and then given one allowance
 * the card is not. **A character the recogniser ranks second still passes**,
 * where it was the right number of strokes in the taught order and only a
 * neighbour came out ahead of it.
 *
 * `grade-handwriting.ts` has why the card holds to first place: the
 * confusions a learner makes put the asked-for character second, and the card
 * exists to tell those apart. The drill is somewhere else in learning a
 * character. A reader is writing it five times running a minute after tracing
 * it, and a reader who wrote 晴 and was told every time that it looked like 腈
 * was being held back by the recogniser rather than by their hand. Hugh asked
 * for the window on 2026-10-15.
 *
 * Both sides mark through here: `src/site/xiezi-check.ts` in the browser on the
 * public page, and `src/user/lianxi-report.ts` in the handler for a signed-in
 * reader's Exercise, so the two can never disagree about a pass.
 */

/**
 * How far down the ranking the asked-for character may come and still pass.
 *
 * Second. Third would take in the pairs `grade-handwriting.ts` measured
 * learners actually confusing, 末 for 未 and 王 for 玉, which sit at second or
 * third, and a drill that passed those would be teaching the confusion.
 */
export const drillRankAllowed = 2;

/** What became of a character written from memory in the drill. */
export interface MarkedDrillWriting {
  /** What the site made of the ink. */
  readonly reading: HandwritingReading;

  /** Whether the pass is passed. */
  readonly passed: boolean;

  /**
   * Whether anything was marked at all. A blank square and a character the
   * index has no strokes for are neither passed nor missed.
   */
  readonly marked: boolean;
}

/**
 * Mark a character written from memory in the drill.
 *
 * @param answer - The character wanted, the ink and the index to rank it in.
 * @returns What the ink was, and whether the pass is passed.
 */
export function markDrillWriting(
  answer: HandwritingAnswer,
): MarkedDrillWriting {
  const { reading, rating } = gradeHandwriting(answer);

  return {
    reading,
    passed: rating === ratings.good || isCloseSecond(answer, reading),
    marked: reading.verdict !== "blank" && reading.verdict !== "unrecognised",
  };
}

/**
 * Whether ink the card would call another character has the asked-for one
 * within {@link drillRankAllowed}.
 *
 * Only an `another` reading is asked. The stroke count already matched for it
 * to be read that way, and the order was not what lost it first place, so
 * what is left is a near neighbour ranked ahead.
 */
function isCloseSecond(
  answer: HandwritingAnswer,
  reading: HandwritingReading,
): boolean {
  if (reading.verdict !== "another") {
    return false;
  }

  return lookUpHandwriting({
    index: answer.index,
    shape: answer.shape,
    limit: drillRankAllowed,
  }).some((candidate) => candidate.character === answer.expected);
}
